import { Stack, Typography } from '@mui/material';
import { type SetStateAction, useCallback, useMemo, useState } from 'react';

import { _getAdminViewsEntitiesInfinite } from '@/api/admin/infinite-queries';
import { AdminEntitiesSortField, AdminEntityType } from '@/api/admin/types';
import { EntityApprovalStatus } from '@/api/entity/types';
import { SortDirection, SortDirectionType } from '@/api/types';
import { getTableMinWidth, Spinner, Table } from '@/design-system';
import useDebounce from '@/global-hooks/use-debounce';
import { useIsMobile } from '@/global-hooks/use-is-mobile';
import { useUrlSyncedState } from '@/global-hooks/use-url-synced-state';
import { legalEntityTypeOptions } from '@/pages/create-entity/common';
import { useInfiniteScroll } from '@/shared/hooks/use-infinite-scroll';

import { EntitiesTabFilters, EntitiesTabFiltersValue } from './components/entities-tab-filters';
import { columns, EntityTabTableRow, URL_KEYS } from './data';

export const EntitiesTab = () => {
  const isMobile = useIsMobile();

  const {
    state: urlState,
    setMany,
    sortBy: sortByRaw,
    sortDirection: sortDirectionRaw,
  } = useUrlSyncedState(URL_KEYS);

  const sortBy = (sortByRaw as AdminEntitiesSortField) || null;
  const sortDirection = (sortDirectionRaw as SortDirectionType) || null;

  const [visibleColumns, setVisibleColumns] = useState<string[]>([
    'legalName',
    'shortName',
    'country',
    'state',
    'type',
    'status',
    'fundsCount',
    'city',
    'zipCode',
    'entityEmail',
    'identificationNumber',
    'teamMembersCount',
    'registrationDate',
    'actions',
  ]);

  const filters: EntitiesTabFiltersValue = useMemo(
    () => ({
      search: urlState.search[0] ?? '',
      status: urlState.status as EntityApprovalStatus[],
      type: urlState.type as AdminEntityType[],
      columns: visibleColumns,
    }),
    [urlState.search, urlState.status, urlState.type, visibleColumns],
  );

  const setFilters = useCallback(
    (action: SetStateAction<EntitiesTabFiltersValue>) => {
      const next = typeof action === 'function' ? action(filters) : action;
      setMany({ search: next.search || undefined, status: next.status, type: next.type }, true);
      setVisibleColumns(next.columns);
    },
    [filters, setMany],
  );

  const debounceSearch = useDebounce(filters.search, 500);

  const {
    items: entities,
    isLoading,
    isFetchingNextPage,
    loadMoreRef,
  } = useInfiniteScroll(
    _getAdminViewsEntitiesInfinite({
      search: debounceSearch?.length > 1 ? debounceSearch : undefined,
      sortBy: sortBy || undefined,
      sortDirection: sortDirection ? (sortDirection.toLowerCase() as 'asc' | 'desc') : undefined,
      status: filters.status.length ? filters.status : undefined,
      type: filters.type.length ? filters.type : undefined,
    }),
    (page) => page.items,
  );

  const rows: EntityTabTableRow[] = useMemo(
    () =>
      entities.map((entity) => ({
        key: entity.id,
        id: entity.id,
        fullName: entity.fullName,
        shortName: entity.shortName || '-',
        country: entity.country,
        stateRegion: entity.stateRegion,
        typeName: legalEntityTypeOptions.find((el) => el.value === entity.type)?.label ?? '-',
        status: entity.status,
        fundsCount: entity.fundsCount,
        city: entity.city,
        zipCode: entity.zipCode,
        entityEmail: entity.entityEmail,
        identificationNumber: entity.identificationNumber,
        teamMembersCount: entity.teamMembersCount,
        registrationDate: entity.registrationDate,
        blockedAt: entity.blockedAt,
        blockReason: entity.blockReason,
        href: `/overview/entity/${entity.id}`,
      })),
    [entities],
  );

  const handleSort = useCallback(
    (key: string) => {
      if (sortBy !== key) {
        setMany({ sortBy: key, sortDirection: SortDirection.ASC });
      } else if (sortDirection === SortDirection.ASC) {
        setMany({ sortDirection: SortDirection.DESC });
      } else {
        setMany({ sortBy: undefined, sortDirection: undefined });
      }
    },
    [sortBy, sortDirection, setMany],
  );

  const filteredColumns = useMemo(
    () => columns.filter((column) => visibleColumns.includes(column.key)),
    [visibleColumns],
  );

  return (
    <Stack flexDirection="column" mt="16px" gap="16px">
      <Typography variant="h2">Entities Summary</Typography>

      <Stack flexDirection="column" alignItems="center" gap="24px">
        <EntitiesTabFilters value={filters} setValue={setFilters} />

        <Stack sx={{ width: '100%', overflowX: 'auto' }}>
          <Stack sx={{ width: '100%', minWidth: isMobile ? 1185 : 'initial' }}>
            <Table
              columns={filteredColumns}
              rows={rows}
              size="lg"
              sortKey={sortBy}
              sortDirection={sortDirection}
              onSort={handleSort}
              isLoading={isLoading}
              minTableWidth={getTableMinWidth(filteredColumns)}
            />
          </Stack>
        </Stack>

        <div ref={loadMoreRef} style={{ height: '20px' }} />

        {isFetchingNextPage && (
          <Stack padding="16px" alignItems="center" justifyContent="center">
            <Spinner />
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};
